import styled from "@emotion/styled";

import memChatAgentTransitionVideoUrl from "@/assets/images/onboarding/mem-chat-agent-transition.mp4";
import {
  FooterButton,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import type { MemAgentHoldToTalkKey } from "@/modules/mem-agent/hold-to-talk-key";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { MemAgentShortcutStepView } from "./mem-agent-shortcut-step";

export interface ExistingUserMemAgentTransitionStepViewProps {
  onComplete: () => void;
}

/** Introduces existing users to the Mem Chat to Mem Agent transition. */
export const ExistingUserMemAgentTransitionStepView = ({
  onComplete,
}: ExistingUserMemAgentTransitionStepViewProps) => (
  <ExistingUserDialogStepWrapper>
    <ExistingUserStepTitle variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
      Mem Chat is now Mem Agent
    </ExistingUserStepTitle>

    <ExistingUserTransitionVideoFrame>
      <ExistingUserVideo
        aria-label="Mem Chat moving into Mem Agent preview"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      >
        <source src={memChatAgentTransitionVideoUrl} type="video/mp4" />
      </ExistingUserVideo>
    </ExistingUserTransitionVideoFrame>

    <ExistingUserBodyCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
      Instead of living in the sidebar of Mem, Mem Agent lives in the corner of your screen.
      It&apos;s smarter and more capable than the previous generation Mem Chat.
    </ExistingUserBodyCopy>

    <ExistingUserFooter>
      <FooterButton onPress={onComplete}>Got it</FooterButton>
    </ExistingUserFooter>
  </ExistingUserDialogStepWrapper>
);

export interface ExistingUserPushToRememberStepViewProps {
  holdToTalkKey: MemAgentHoldToTalkKey;
  isSavingHoldToTalkKey?: boolean;
  isWindows?: boolean;
  onHoldToTalkKeyChange: (nextKey: MemAgentHoldToTalkKey) => void;
}

/** Teaches existing users the Push-to-Remember hold-to-talk shortcut. */
export const ExistingUserPushToRememberStepView = ({
  holdToTalkKey,
  isSavingHoldToTalkKey = false,
  isWindows,
  onHoldToTalkKeyChange,
}: ExistingUserPushToRememberStepViewProps) => (
  <MemAgentShortcutStepView
    holdToTalkKey={holdToTalkKey}
    isSavingHoldToTalkKey={isSavingHoldToTalkKey}
    isWindows={isWindows}
    onHoldToTalkKeyChange={onHoldToTalkKeyChange}
  />
);

const ExistingUserDialogStepWrapper = styled(WizardContentWrapper)`
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ExistingUserStepTitle = styled(MDSText)`
  display: block;
  color: ${({ theme }) => theme.colors.text.contrast};
  word-break: break-word;
`;

const ExistingUserVideoFrame = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const ExistingUserTransitionVideoFrame = styled(ExistingUserVideoFrame)`
  aspect-ratio: 1200 / 900;
`;

const ExistingUserVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const ExistingUserBodyCopy = styled(MDSText)`
  display: block;
  width: 100%;
`;

const ExistingUserFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing[1]};
`;
